import { forwardRef } from 'react';

interface SectionProps {
  id: string;
  className?: string;
  children: React.ReactNode;
}

const Section = forwardRef<HTMLElement, SectionProps>(({ id, className = '', children }, ref) => (
  <section id={id} ref={ref} className={`py-14 md:py-20 px-4 md:px-6 ${className}`}>
    <div className="max-w-7xl mx-auto">{children}</div>
  </section>
));

Section.displayName = 'Section';

/* ───── #about: 品牌介绍 1项 ───── */
export function AboutSection() {
  return (
    <Section id="about" className="bg-background-100">
      <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-100 text-center mb-3">
        关于我们
      </h2>
      <p className="text-sm text-foreground-500 text-center max-w-lg mx-auto mb-10">
        专业团队运营，致力打造极致观影体验
      </p>
      <div className="max-w-3xl mx-auto bg-background-50 rounded-2xl border border-background-300/30 p-6 md:p-10">
        <div className="flex flex-col md:flex-row gap-8 items-center">
          <div className="w-20 h-20 md:w-24 md:h-24 flex-shrink-0 rounded-2xl bg-primary-500/10 flex items-center justify-center">
            <i className="ri-movie-2-fill text-3xl md:text-4xl text-primary-400"></i>
          </div>
          <div>
            <h3 className="text-lg md:text-xl font-heading font-bold text-foreground-100 mb-3">
              91电影 — 您的私人高清在线影院
            </h3>
            <p className="text-sm md:text-base text-foreground-400 leading-relaxed">
              91电影在线观看与在线播放平台成立于2019年，专注为华语观众提供高清流畅的免费在线观影服务。
              我们拥有超过12,000部影视资源，每日更新36部以上国产热门大片，覆盖动作片、喜剧片、
              悬疑片、科幻片、爱情片等全类型。采用分布式CDN加速技术，确保全国用户都能享受秒播不卡顿
              的观影体验。无需注册、无需会员，打开浏览器即可畅享影院级画质。
            </p>
          </div>
        </div>
      </div>
    </Section>
  );
}

/* ───── #app: 移动端入口 1项 ───── */
export function AppSection() {
  return (
    <Section id="app" className="bg-background-50">
      <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-100 text-center mb-10">
        手机观影
      </h2>
      <div className="max-w-4xl mx-auto bg-gradient-to-br from-background-100 to-background-200/50 rounded-2xl border border-background-300/30 overflow-hidden">
        <div className="flex flex-col md:flex-row items-center">
          <div className="w-full md:w-1/2 p-8 md:p-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-500/10 border border-accent-500/20 mb-4">
              <i className="ri-smartphone-line text-accent-400 text-xs"></i>
              <span className="text-xs font-medium text-accent-400 whitespace-nowrap">移动端适配</span>
            </div>
            <h3 className="text-xl md:text-2xl font-heading font-bold text-foreground-100 mb-4">
              随时随地 · 畅享影院
            </h3>
            <p className="text-sm text-foreground-400 leading-relaxed mb-6">
              91电影在线播放平台全面适配手机、平板等移动设备。无论是在通勤路上、午休间隙
              还是睡前放松，打开手机浏览器即可畅享高清在线影院体验。支持横竖屏自动切换，
              触控友好，流量消耗经优化处理，让您在移动网络下也能流畅观影。
            </p>
            <div className="flex flex-wrap gap-3">
              <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-50 border border-background-300/30 text-xs text-foreground-300 whitespace-nowrap">
                <i className="ri-apple-fill text-sm text-foreground-400"></i>
                iOS Safari
              </span>
              <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-50 border border-background-300/30 text-xs text-foreground-300 whitespace-nowrap">
                <i className="ri-android-fill text-sm text-foreground-400"></i>
                Android Chrome
              </span>
              <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-50 border border-background-300/30 text-xs text-foreground-300 whitespace-nowrap">
                <i className="ri-tablet-line text-sm text-foreground-400"></i>
                iPad
              </span>
            </div>
          </div>
          <div className="w-full md:w-1/2 p-6 md:p-8 flex justify-center">
            <div className="relative w-48 h-80 md:w-56 md:h-96 bg-background-300/30 rounded-3xl border-2 border-background-300/40 overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-8 bg-background-950/40 flex items-center justify-center">
                <div className="w-16 h-1 rounded-full bg-background-500/50"></div>
              </div>
              <div className="pt-10 px-3 space-y-2">
                <div className="h-28 bg-background-300/30 rounded-lg flex items-center justify-center">
                  <i className="ri-play-circle-fill text-3xl text-primary-400/60"></i>
                </div>
                <div className="h-2 w-3/4 bg-background-300/30 rounded"></div>
                <div className="h-2 w-1/2 bg-background-300/20 rounded"></div>
                <div className="h-28 bg-background-300/30 rounded-lg mt-3 flex items-center justify-center">
                  <i className="ri-play-circle-fill text-3xl text-primary-400/60"></i>
                </div>
                <div className="h-2 w-3/4 bg-background-300/30 rounded"></div>
                <div className="h-2 w-1/2 bg-background-300/20 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

/* ───── #features: 特色卡片 3项 ───── */
export function FeaturesSection() {
  const features = [
    {
      icon: 'ri-speed-up-fill',
      title: '秒播不卡顿',
      desc: '分布式CDN节点覆盖全国，智能选路技术确保高清流畅播放，打开即看无需缓冲等待，带来91电影在线播放的极致速度体验。',
    },
    {
      icon: 'ri-shield-star-fill',
      title: '安全无广告',
      desc: '纯净观影环境，无弹窗广告、无恶意跳转。所有资源经安全检测，让您放心享受91电影免费在线的高品质内容。',
    },
    {
      icon: 'ri-refresh-fill',
      title: '每日同步更新',
      desc: '与院线及视频平台同步更新，每日新增36+部国产大片。热门新片第一时间上线，91电影在线观看永不错过精彩。',
    },
  ];

  return (
    <Section id="features" className="bg-background-100">
      <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-100 text-center mb-3">
        平台特色
      </h2>
      <p className="text-sm text-foreground-500 text-center max-w-lg mx-auto mb-10">
        为什么千万用户选择91电影
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {features.map((f) => (
          <div
            key={f.title}
            className="bg-background-50 rounded-2xl border border-background-300/30 p-6 md:p-8 hover:border-primary-500/20 transition-colors group"
          >
            <div className="w-12 h-12 rounded-xl bg-primary-500/10 flex items-center justify-center mb-5 group-hover:bg-primary-500/20 transition-colors">
              <i className={`${f.icon} text-xl text-primary-400`}></i>
            </div>
            <h3 className="text-lg font-heading font-bold text-foreground-100 mb-3">{f.title}</h3>
            <p className="text-sm text-foreground-500 leading-relaxed">{f.desc}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ───── #updates: 动态时间线 8项 ───── */
export function UpdatesSection() {
  const updates = [
    { date: '07-29', tag: '新片上线', title: '《烈火追击》高清完整版现已上线', color: 'primary' },
    { date: '07-29', tag: '片源更新', title: '《暗夜追踪》4K修复版独家首发', color: 'accent' },
    { date: '07-28', tag: '热门推荐', title: '《孤胆英雄》票房冠军限时开放', color: 'primary' },
    { date: '07-28', tag: '系统升级', title: 'CDN节点扩容完成，播放速度提升40%', color: 'secondary' },
    { date: '07-27', tag: '新片上线', title: '《深海迷踪》悬疑大片今日首播', color: 'primary' },
    { date: '07-27', tag: '专题策划', title: '「暑期档必看」专题合集更新12部', color: 'accent' },
    { date: '07-26', tag: '片源更新', title: '《极速营救》加长版导演剪辑版上线', color: 'primary' },
    { date: '07-26', tag: '热门推荐', title: '豆瓣8.5分《山野回声》口碑佳作推荐', color: 'accent' },
  ];

  const getTagClass = (color: string) => {
    if (color === 'primary') return 'bg-primary-500/10 text-primary-400 border-primary-500/20';
    if (color === 'accent') return 'bg-accent-500/10 text-accent-400 border-accent-500/20';
    return 'bg-secondary-500/10 text-secondary-400 border-secondary-500/20';
  };

  return (
    <Section id="updates" className="bg-background-50">
      <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-100 text-center mb-3">
        更新动态
      </h2>
      <p className="text-sm text-foreground-500 text-center max-w-lg mx-auto mb-10">
        最新片源与平台资讯一手掌握
      </p>
      <div className="max-w-3xl mx-auto">
        {updates.map((u, i) => (
          <div key={i} className="relative pl-8 pb-8 last:pb-0">
            {i < updates.length - 1 && (
              <div className="absolute left-[11px] top-8 bottom-0 w-px bg-background-300/40"></div>
            )}
            <div className="absolute left-0 top-1 w-[23px] h-[23px] rounded-full border-2 border-primary-500/40 bg-background-50 flex items-center justify-center">
              <div className="w-2 h-2 rounded-full bg-primary-500"></div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <span className="text-xs font-mono text-foreground-500 whitespace-nowrap">{u.date}</span>
              <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap ${getTagClass(u.color)}`}>
                {u.tag}
              </span>
              <span className="text-sm text-foreground-300 font-medium">{u.title}</span>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}